import { Button, Chip, Divider, styled, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { Assembly, WarehouseId } from '../../../../models/models'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setAssemblyDispatchView } from '../../../../store/warehouse-map-slice'
import { dispatchTransferAssemblyRequest } from '../../../../api/logistics-worksheet'
import { toastSuccess, toastWarn } from '../../../../util/toast'
import { Urls } from '../../../../util/urls'
import { ActiveModal } from './Transfer'
import { isFlatstation } from '../../../../util/util'

interface TransferCardProps {
    item: Assembly
    warehouseId: WarehouseId
    onClick: () => void
    openModal: (activeModal: ActiveModal, item: Assembly) => void
    openDetailsModal: (item: Assembly) => void
}

const CardRow = (props: { description: string; content: string; disableDivider?: boolean }) => {
    const Row = styled(Box)(() => ({
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginTop: '10px',
    }))

    const RowContent = styled(Box)(() => ({
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        marginBottom: '6px',
    }))

    const RowDescriptionContainer = styled(Box)(() => ({
        paddingLeft: '10px',
        width: '50%',
    }))

    const RowContentContainer = styled(Box)(() => ({
        width: '50%',
    }))

    const { description, content, disableDivider } = props
    return (
        <Row>
            <RowContent>
                <RowDescriptionContainer>
                    <Typography sx={{ color: '#26262690' }}>{description}</Typography>
                </RowDescriptionContainer>
                <RowContentContainer>
                    <Typography>{content}</Typography>
                </RowContentContainer>
            </RowContent>
            {!disableDivider && <Divider />}
        </Row>
    )
}

const CardHeader = (props: {
    assembly: Assembly
    openDetailsModal: (assembly: Assembly) => void
}) => {
    const { assembly, openDetailsModal } = props
    const Row = styled(Box)(() => ({
        backgroundColor: '#E5F6DF',
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        justifyContent: 'space-between',
        padding: '5px',
    }))

    const ProjectName = styled(Box)(() => ({
        justifyContent: 'center',
        alignSelf: 'center',
        paddingLeft: '10px',
    }))

    const DetailsButton = styled(Button)(() => ({
        padding: '0px',
        minWidth: '0px',
    }))

    return (
        <div>
            <Row>
                <ProjectName>{assembly.callOff?.project?.name}</ProjectName>
                <DetailsButton
                    variant="text"
                    size="small"
                    onClick={() => openDetailsModal(assembly)}
                >
                    <Typography>Inspect Assembly</Typography>
                </DetailsButton>
            </Row>
            <Row>
                {(assembly.callOff.isOnHold || assembly.isOnHold) && (
                    <Chip
                        sx={{
                            marginTop: '10px',
                            color: '#ff1a1a',
                            whiteSpace: 'normal',
                            flexWrap: 'wrap',
                            borderColor: '#ff1a1a',
                            backgroundColor: '#ffcccc',
                        }}
                        label="Is on hold"
                        variant="outlined"
                    />
                )}
            </Row>
            <Row>
                {assembly.callOff?.isOnHold && (
                    <Chip
                        sx={{
                            marginTop: '10px',
                            color: '#ff1a1a',
                            whiteSpace: 'normal',
                            flexWrap: 'wrap',
                            borderColor: '#ff1a1a',
                            backgroundColor: '#ffcccc',
                        }}
                        label="Is on hold"
                        variant="outlined"
                    />
                )}
            </Row>
        </div>
    )
}

const CardFooter = (props: { buttonText: string; onClick: () => void }) => {
    const { onClick, buttonText } = props
    const Row = styled(Box)(() => ({
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        justifyContent: 'flex-end',
    }))

    const TransferButton = styled(Button)(() => ({
        padding: '0px',
        minWidth: '0px',
        margin: '10px 10px 5px 0px',
    }))

    return (
        <Row>
            <TransferButton variant="outlined" size="small" onClick={onClick}>
                {buttonText}
            </TransferButton>
        </Row>
    )
}

interface CardContainerProps {
    dateToBeCompletedBy: Date
}

const CardContainer = styled(Box)<CardContainerProps>((props: CardContainerProps) => ({
    border: moment(props.dateToBeCompletedBy).isBefore(moment(), 'day') ? 'solid 1px red' : 'none',
    display: 'flex',
    flexDirection: 'column',
    width: '380px',
    margin: '20px',
    paddingBottom: '10px',
    backgroundColor: '#FFF',
    boxShadow: `rgba(100, 100, 111, 0.2) 0px 7px 29px 0px`,
}))

export default function DispatchTransferCard(props: TransferCardProps) {
    const { item, openDetailsModal, onClick } = props
    item.dateToBeCompletedBy
    const dispatch = useDispatch()
    const history = useHistory()
    return (
        <CardContainer dateToBeCompletedBy={props.item.dateToBeCompletedBy}>
            <CardHeader assembly={item} openDetailsModal={openDetailsModal} />
            <CardRow description="Assembly id" content={item.id.toString()} />
            <CardRow description="Call off id" content={item.callOffId.toString()} />
            <CardRow
                description="Product"
                content={item?.builtItemCallOff.sopBuiltItemOrderline?.builtItem.bomCode}
            />

            <CardRow description="Amount" content={item.amount.toString()} />

            <CardRow
                description="Call off customer dispatch"
                content={moment(item.callOff.customerDispatchDate).format('DD/MM')}
            />

            <CardFooter
                buttonText="Dispatch transfer"
                onClick={async () => {
                    if (item.callOff.isOnHold) {
                        toastWarn('Cannot transfer since the worksheet is on hold')
                        return
                    }
                    if (item.isOnHold) {
                        toastWarn('Cannot transfer since the worksheet is on hold')
                        return
                    }
                    if (isFlatstation(item?.builtItemCallOff.sopBuiltItemOrderline?.builtItem)) {
                        dispatch(setAssemblyDispatchView(item))
                        history.push(Urls.WarehouseMap)
                    } else {
                        await dispatchTransferAssemblyRequest(item.id)
                        toastSuccess('Built item sent to dispatch area')
                        onClick()
                    }
                }}
            ></CardFooter>
        </CardContainer>
    )
}
